import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dms, inr, niceDate } from "./api";
import { PageHeader } from "./OwnerPages";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronRight, ClipboardList, LogOut, MapPin, Target, X } from "lucide-react";

export const TARGET_LABELS = { visits: "Visits", order_value: "Order (₹)", order_liters: "Order (Litre)", collection: "Collection (₹)" };
export const TARGET_KEYS = Object.keys(TARGET_LABELS);
const RETRIES = 2;
const RETRY_GAP_MS = 2000;
const QUEUE_KEY = "dms_punch_out_queue";
const DISMISS_KEY = "dms_pending_report_dismissed";
export const OSM_NOTE = "Area names: © OpenStreetMap contributors";

export const STATUS_LABEL = {
  open: "In progress", submitted: "Submitted", incomplete_auto: "Incomplete - auto closed", pending: "Pending (to be filled later)",
  completed_late: "Filled later", locked: "Locked", in_progress: "In progress", no_report: "No report", no_plan: "No plan filled",
};

export const fmtValue = (key, v) => {
  if (v == null) return "—";
  if (key === "order_value" || key === "collection") return inr(v);
  if (key === "order_liters") return `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
  return Number(v).toLocaleString("en-IN");
};
// Morning questions and their punch-out pair. `sys` = what the system counted itself.
export const PLAN_ITEMS = [
  { k: "places", plan: "Where are you going today?", did: "Where did you go?", text: true },
  { k: "km_one_way", plan: "How many km is it from your starting point to there?", did: "Km from your starting point to there", optional: true },
  { k: "km_total", plan: "Total km for the day (going and coming back)?", did: "Total km you travelled today", sys: "km" },
  { k: "order_value", plan: "How much order value can you bring today? (₹)", did: "Order value you brought (₹)", sys: "order_value" },
  { k: "order_liters", plan: "How many litres of order can you bring today?", did: "Litres of order you brought", sys: "order_liters" },
];
const ITEM_FMT = { km_one_way: (v) => `${Number(v || 0).toLocaleString("en-IN")} km`, km_total: (v) => `${Number(v || 0).toLocaleString("en-IN")} km`,
  order_value: (v) => inr(v || 0), order_liters: (v) => `${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L` };
export const fmtItem = (k, v) => (v == null || v === "" ? "—" : k === "places" ? v : ITEM_FMT[k](v));

// "You said | You did" for one day. Editable when setActual is given (punch-out), read-only otherwise.
export function SaidDidTable({ answers, actual, setActual, achieved }) {
  return (
    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="said-did">
      <div className="grid grid-cols-2 gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
        <span>You said (morning)</span><span>You did</span>
      </div>
      {PLAN_ITEMS.map((it) => {
        const sysV = it.sys ? achieved?.[it.sys] : null;
        return (
          <div key={it.k} className="px-3 py-2">
            <div className="text-xs text-slate-600 mb-1">{setActual ? it.did : it.plan}{setActual && !it.optional ? " *" : ""}</div>
            <div className="grid grid-cols-2 gap-2 items-start">
              <div className="h-10 px-3 rounded-lg bg-slate-100 text-sm flex items-center font-medium text-slate-800 truncate">{fmtItem(it.k, answers?.[it.k])}</div>
              {setActual ? (
                <Input type={it.text ? "text" : "number"} inputMode={it.text ? "text" : "decimal"} min="0" value={actual?.[it.k] ?? ""}
                  onChange={(e) => setActual((a) => ({ ...a, [it.k]: e.target.value }))} data-testid={`did-${it.k}`} />
              ) : (
                <div className="h-10 px-3 rounded-lg border border-slate-200 text-sm flex items-center font-medium truncate">{fmtItem(it.k, actual?.[it.k])}</div>
              )}
            </div>
            {it.sys && <div className="text-[11px] text-slate-500 mt-1">System count: <b>{fmtItem(it.k, sysV ?? 0)}</b></div>}
          </div>
        );
      })}
    </div>
  );
}

// Plan vs Actual table cells: planned / said / system for one item.
export const PVA_COLS = [
  { k: "places", label: "Place" }, { k: "km_total", label: "Km", sys: "km" },
  { k: "order_value", label: "Order (₹)", sys: "order_value" }, { k: "order_liters", label: "Order (Litre)", sys: "order_liters" },
];
export function PvaCell({ r, col }) {
  const plan = r.answers ? r.answers[col.k] : (col.k === "km_total" ? null : col.k === "places" ? (r.areas || []).join(", ") : r.targets?.[col.k]);
  const said = r.self_actual?.[col.k];
  if (col.k === "places") {
    return (
      <span className="block max-w-[180px]">
        <span className="block truncate">{plan || "—"}</span>
        {said != null && <span className="block text-[11px] text-slate-500 truncate">Went: {said}</span>}
      </span>
    );
  }
  const sysV = r.achieved?.[col.sys];
  const p = r.pct?.[col.sys];
  const low = p != null && p < 50 && r.status !== "in_progress";
  return (
    <span className="whitespace-nowrap">
      <span className="block">Plan {fmtItem(col.k, plan)}</span>
      <span className="block text-[11px] text-slate-600">Said {fmtItem(col.k, said)}</span>
      <span className={`block text-[11px] ${low ? "text-rose-700 font-semibold" : p >= 100 ? "text-emerald-700" : "text-slate-500"}`}>
        System {fmtItem(col.k, sysV)}{p != null ? ` · ${p}%` : ""}
      </span>
    </span>
  );
}

export const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const detailOf = (e, fallback) => e?.response?.data?.detail || fallback;
const isRetryable = (e) => !e?.response || e.response.status >= 500;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const readStore = (key, store = window.localStorage) => {
  try { return JSON.parse(store.getItem(key)); } catch (e) { return null; }
};
const writeStore = (key, value, store = window.localStorage) => {
  try {
    if (value == null) store.removeItem(key); else store.setItem(key, JSON.stringify(value));
  } catch (e) { /* storage blocked: the app still works, only the retry is lost */ }
};

const getFix = (timeout = 15000) => new Promise((resolve) => {
  if (!("geolocation" in navigator)) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout, maximumAge: 0 },
  );
});

// Retries only failures the salesman cannot fix; a 4xx is returned at once.
async function withRetry(fn) {
  let last;
  for (let i = 0; i <= RETRIES; i += 1) {
    try { return await fn(); } catch (e) {
      last = e;
      if (!isRetryable(e)) throw e;
      if (i < RETRIES) await wait(RETRY_GAP_MS);
    }
  }
  throw last;
}

// A punch out the server could not receive waits on the phone and goes when it can.
export async function flushPunchOutQueue() {
  const item = readStore(QUEUE_KEY);
  if (!item) return;
  try {
    await dms.punchOut(item);
    writeStore(QUEUE_KEY, null);
    window.dispatchEvent(new Event("dms-punch-changed"));
  } catch (e) {
    if (!isRetryable(e)) writeStore(QUEUE_KEY, null);
  }
}

// ── Plan form ──────────────────────────────────────────────────────────
function PlanForm({ initial, floor, onSaved, submitLabel }) {
  const [f, setF] = useState(() => Object.fromEntries(PLAN_ITEMS.map((it) => [it.k, String(initial?.answers?.[it.k] ?? "")])));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await (initial ? dms.dayPlanEdit(f) : dms.dayPlanCreate(f));
      toast.success(initial ? "Plan updated" : "Today's plan saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(detailOf(e, "Plan was not saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  const minOf = { order_value: floor?.order_value, order_liters: floor?.order_liters };
  return (
    <div className="space-y-4">
      {PLAN_ITEMS.map((it) => (
        <div key={it.k}>
          <Label>{it.plan}{it.optional ? "" : " *"}</Label>
          <Input className="mt-1" type={it.text ? "text" : "number"} inputMode={it.text ? "text" : "decimal"} min={minOf[it.k] || 0}
            value={f[it.k]} onChange={(e) => setF((x) => ({ ...x, [it.k]: e.target.value }))}
            placeholder={it.text ? "e.g. Vapi GIDC, Chala" : ""} data-testid={`plan-${it.k}`} />
          {minOf[it.k] > 0 && <div className="text-[11px] text-slate-500 mt-0.5">Your target: at least {fmtItem(it.k, minOf[it.k])}</div>}
        </div>
      ))}
      <Button onClick={submit} disabled={busy} className="w-full bg-[#a67c00] hover:bg-[#8a6600]" data-testid="plan-submit">
        {busy ? "Saving…" : submitLabel}
      </Button>
    </div>
  );
}

// ── Shop rows shared by the report screens ─────────────────────────────
function MissedReason({ row, value, reasons, onChange }) {
  return (
    <div className="mt-2 space-y-2">
      <select value={value?.reason || ""} onChange={(e) => onChange({ ...value, reason: e.target.value })}
        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid={`reason-${row.retailer_id}`}>
        <option value="">Select a reason *</option>
        {Object.entries(reasons || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      {value?.reason === "other" && (
        <Input value={value?.text || ""} onChange={(e) => onChange({ ...value, text: e.target.value })} placeholder="Type the reason *" data-testid={`reason-text-${row.retailer_id}`} />
      )}
    </div>
  );
}

export function ShopRow({ row, children }) {
  const visited = row.status === "visited";
  let detail;
  if (!visited) detail = row.has_location ? "Not visited" : "Not visited (shop location not saved)";
  else if (row.evidence === "gps") detail = `${timeOf(row.time_in)} – ${timeOf(row.time_out)} · ${row.minutes} min`;
  else detail = row.evidence === "order" ? "Confirmed by order" : "Confirmed by payment";
  return (
    <div className="px-3 py-2 text-sm">
      <div className="flex items-start gap-2">
        {visited ? <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" /> : <X size={16} className="text-rose-600 shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900">{row.name}</div>
          <div className="text-xs text-slate-500">{detail}</div>
          {row.reason_label && <div className="text-xs text-rose-700 mt-0.5">{row.reason_label}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

export function Scorecard({ targets, achieved, pct, showKm }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {TARGET_KEYS.map((k) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2.5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{TARGET_LABELS[k]}</div>
          <div className="font-bold text-slate-900">{fmtValue(k, achieved?.[k])}</div>
          <div className="text-[11px] text-slate-500">
            Target {fmtValue(k, targets?.[k])}{pct?.[k] != null ? ` · ${pct[k]}%` : ""}
          </div>
        </div>
      ))}
      {showKm && (
        <div className="rounded-lg border border-slate-200 p-2.5 col-span-2 text-sm">
          Total: <b>{achieved?.km ?? 0} km</b>
        </div>
      )}
    </div>
  );
}

function ReportBody({ data, reasons, picks, setPicks, remark, setRemark, actual, setActual }) {
  const shops = data?.shops || [];
  const targets = data?.plan?.targets || data?.plan_targets;
  const answers = data?.plan?.answers || data?.plan_answers;
  return (
    <div className="space-y-4">
      {answers ? <SaidDidTable answers={answers} actual={actual} setActual={setActual} achieved={data?.achieved} />
        : <Scorecard targets={targets} achieved={data?.achieved} pct={data?.pct} showKm />}
      {shops.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Planned shops</div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {shops.map((row) => (
              <ShopRow key={row.retailer_id} row={row}>
                {row.status === "missed" && (
                  <MissedReason row={row} reasons={reasons} value={picks[row.retailer_id]}
                    onChange={(v) => setPicks((p) => ({ ...p, [row.retailer_id]: v }))} />
                )}
              </ShopRow>
            ))}
          </div>
        </div>
      )}
      {(data?.unplanned || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Unplanned stops</div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {data.unplanned.map((s) => (
              <div key={s.in_at} className="px-3 py-2 text-sm flex items-start gap-2">
                <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500">{timeOf(s.in_at)} – {timeOf(s.out_at)} · {s.minutes} min</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">{OSM_NOTE}</div>
        </div>
      )}
      <div>
        <Label>Remark for the day (optional)</Label>
        <Textarea className="mt-1" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} data-testid="report-remark" />
      </div>
    </div>
  );
}

const picksFrom = (shops) => Object.fromEntries((shops || []).filter((r) => r.reason).map((r) => [r.retailer_id, { reason: r.reason, text: r.reason_text || "" }]));

// ── Din ki Report: shown on manual Punch Out ───────────────────────────
export function DayReportScreen({ open, punchId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});
  const [remark, setRemark] = useState("");
  const [actual, setActual] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [canDefer, setCanDefer] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    setData(null); setNote(""); setCanDefer(false); setBusy(true);
    try {
      const d = await withRetry(() => dms.dayReportDraft());
      setData(d); setPicks(picksFrom(d.shops)); setRemark(d.remark || "");
    } catch (e) {
      if (isRetryable(e)) {
        setNote("Could not load the report. There is a network or server problem.");
        setCanDefer(true);
      } else {
        toast.error(detailOf(e, "Could not load the report"));
        onCloseRef.current();
      }
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const finish = () => {
    window.dispatchEvent(new Event("dms-punch-changed"));
    onDone && onDone();
  };

  const submit = async () => {
    setBusy(true); setNote(""); setCanDefer(false);
    const fix = await getFix();
    if (!fix) {
      setNote("Could not get your location. Try again in an open place, or fill the report later.");
      setCanDefer(true); setBusy(false);
      return;
    }
    try {
      const hasQ = !!data?.plan?.answers;
      await withRetry(() => dms.dayReportSubmit({ ...fix, reasons: picks, remark, ...(hasQ ? { actual } : {}) }));
      toast.success("Report saved and punched out");
      finish();
    } catch (e) {
      if (isRetryable(e)) {
        setNote("Report was not saved. There is a network or server problem.");
        setCanDefer(true);
      } else {
        toast.error(detailOf(e, "Report was not saved"));
      }
    } finally {
      setBusy(false);
    }
  };

  const reportLater = async () => {
    setBusy(true);
    const pressedAt = new Date().toISOString();
    const fix = await getFix(5000);
    const pid = data?.punch_id || punchId;
    const body = { report_later: true, client_out_at: pressedAt, ...(pid ? { punch_id: pid } : {}), ...(fix || {}) };
    try {
      await dms.punchOut(body);
      toast.success("Punched out. Please fill the report within 24 hours.");
    } catch (e) {
      if (isRetryable(e)) {
        writeStore(QUEUE_KEY, body);
        toast.warning("No network. You will be punched out with this time as soon as the network is back.", { duration: 10000 });
      } else {
        toast.error(detailOf(e, "Punch out failed"));
        setBusy(false);
        return;
      }
    }
    setBusy(false);
    finish();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Day Report</DialogTitle></DialogHeader>
        {!data && busy && <div className="py-8 text-center text-sm text-slate-500">Preparing report…</div>}
        {data && <ReportBody data={data} reasons={data.reasons} picks={picks} setPicks={setPicks} remark={remark} setRemark={setRemark} actual={actual} setActual={setActual} />}
        {note && <div className="text-sm text-rose-700 bg-rose-50 rounded-lg p-2.5" data-testid="report-error">{note}</div>}
        <DialogFooter className="flex-col sm:flex-col gap-2">
          {data && (
            <Button onClick={submit} disabled={busy} className="w-full bg-rose-700 hover:bg-rose-800" data-testid="report-submit">
              {busy ? "Saving…" : "Submit report and Punch Out"}
            </Button>
          )}
          {!data && canDefer && <Button onClick={load} disabled={busy} variant="outline" className="w-full">Try again</Button>}
          {canDefer && (
            <Button onClick={reportLater} disabled={busy} variant="outline" className="w-full border-rose-300 text-rose-700" data-testid="report-later">
              Fill report later (Punch Out now)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Adhoori report: filled within 24 hours ─────────────────────────────
function PendingReportDialog({ pending, reasons, today, onClose, onDone }) {
  const [picks, setPicks] = useState(() => picksFrom(pending.shops));
  const [remark, setRemark] = useState(pending.remark || "");
  const [actual, setActual] = useState(() => pending.self_actual || {});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await dms.dayReportComplete({ date: pending.date, reasons: picks, remark, ...(pending.plan_answers ? { actual } : {}) });
      toast.success("Report saved");
      onDone();
    } catch (e) {
      toast.error(detailOf(e, "Report was not saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pending-report">
        <DialogHeader>
          <DialogTitle>{pending.date === today ? "Today's" : "Yesterday's"} report is incomplete ({pending.date})</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Please fill it by {niceDate(pending.deadline)}. After that the report will be locked.
        </p>
        <ReportBody data={pending} reasons={reasons} picks={picks} setPicks={setPicks} remark={remark} setRemark={setRemark} actual={actual} setActual={setActual} />
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button onClick={submit} disabled={busy} className="w-full bg-[#a67c00] hover:bg-[#8a6600]" data-testid="pending-submit">
            {busy ? "Saving…" : "Save report"}
          </Button>
          <Button onClick={onClose} disabled={busy} variant="outline" className="w-full">Later</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Gate: runs on every DMS page, like FieldLocationGuard ──────────────
export default function DayPlanGate() {
  const { user, impersonation, logout } = useAuth();
  const active = !!user && !impersonation && user.role === "salesperson";
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null);

  const refresh = useCallback(() => {
    // A failed call leaves the app open: a server problem must not stop the field team.
    dms.dayPlanToday().then(setState).catch(() => setState(null));
    dms.dayPendingReport().then((d) => {
      if (!d.report) { setPending(null); return; }
      const dismissed = readStore(DISMISS_KEY, window.sessionStorage);
      setPending(dismissed === d.report.date ? null : d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    flushPunchOutQueue();
    refresh();
    const t = setInterval(() => { flushPunchOutQueue(); refresh(); }, 60000);
    const onOnline = () => flushPunchOutQueue();
    window.addEventListener("dms-punch-changed", refresh);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(t);
      window.removeEventListener("dms-punch-changed", refresh);
      window.removeEventListener("online", onOnline);
    };
  }, [active, refresh]);

  if (!active) return null;

  if (state?.needs_plan) {
    return (
      <div role="dialog" aria-modal="true" data-testid="day-plan-gate"
        style={{ position: "fixed", inset: 0, zIndex: 2147481000, background: "#f8fafc", overflowY: "auto" }}>
        <div className="max-w-lg mx-auto p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList size={20} /> Today's Plan</h2>
            {logout && (
              <Button variant="outline" size="sm" onClick={() => logout()} data-testid="day-plan-logout">
                <LogOut size={14} className="mr-1" /> Logout
              </Button>
            )}
          </div>
          <p className="text-sm text-slate-600 mb-4">The app will open after you fill the plan.</p>
          <Card className="p-4">
            <PlanForm floor={state.floor} onSaved={refresh} submitLabel="Save plan and continue" />
          </Card>
        </div>
      </div>
    );
  }
  if (pending) {
    return (
      <PendingReportDialog
        key={pending.report.date}
        pending={pending.report} reasons={pending.reasons} today={pending.today}
        onClose={() => { writeStore(DISMISS_KEY, pending.report.date, window.sessionStorage); setPending(null); }}
        onDone={() => { setPending(null); refresh(); }}
      />
    );
  }
  return null;
}

// ── Home card ──────────────────────────────────────────────────────────
export function DayPlanCard() {
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const load = useCallback(() => { dms.dayPlanToday().then(setState).catch(() => {}); }, []);
  useEffect(() => {
    load();
    dms.dayReports().then((d) => setReports(d.data || [])).catch(() => {});
    const t = setInterval(load, 60000);
    window.addEventListener("dms-punch-changed", load);
    return () => { clearInterval(t); window.removeEventListener("dms-punch-changed", load); };
  }, [load]);

  const plan = state?.plan;
  const done = (state?.shops || []).filter((r) => r.status === "visited").length;
  return (
    <>
      {plan && (
        <Card className="p-4 mb-4" data-testid="day-plan-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-slate-900 flex items-center gap-2"><Target size={16} className="text-[#a67c00]" /> Today's Plan</div>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="plan-edit">Edit plan</Button>
          </div>
          {plan.answers ? (
            <div className="grid grid-cols-2 gap-2">
              {PLAN_ITEMS.filter((it) => it.sys).map((it) => (
                <div key={it.k} className="rounded-lg border border-slate-200 p-2.5">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{it.k === "km_total" ? "Km" : it.k === "order_value" ? "Order (₹)" : "Order (Litre)"}</div>
                  <div className="font-bold text-slate-900">{fmtItem(it.k, state.achieved?.[it.sys] ?? 0)}</div>
                  <div className="text-[11px] text-slate-500">Plan {fmtItem(it.k, plan.answers[it.k])}{state.pct?.[it.sys] != null ? ` · ${state.pct[it.sys]}%` : ""}</div>
                </div>
              ))}
            </div>
          ) : <Scorecard targets={plan.targets} achieved={state.achieved} pct={state.pct} />}
          {state.shops.length > 0 && (
            <div className="mt-3 text-sm text-slate-700">
              Shops visited: <b>{done} / {state.shops.length}</b>
              {state.next_shop && <div className="mt-1">Next shop: <b>{state.next_shop.name}</b></div>}
            </div>
          )}
          {plan.areas?.length > 0 && <div className="mt-1 text-xs text-slate-500">{plan.answers ? "Going to" : "Areas"}: {plan.areas.join(", ")}</div>}
        </Card>
      )}
      {reports.length > 0 && (
        <Card className="p-4 mb-4">
          <button className="w-full flex items-center justify-between font-semibold text-slate-900" onClick={() => setShowReports((v) => !v)} data-testid="my-reports-toggle">
            <span>Reports of the last 7 days ({reports.length})</span>
            {showReports ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {showReports && (
            <div className="mt-3 divide-y divide-slate-100">
              {reports.map((r) => (
                <div key={r.date} className="py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <b>{r.date}</b>
                    <span className="text-xs text-slate-500">{STATUS_LABEL[r.status] || r.status}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {r.achieved?.visits ?? 0} visits · {inr(r.achieved?.order_value || 0)} · {r.achieved?.order_liters ?? 0} L · collection {inr(r.achieved?.collection || 0)} · {r.achieved?.km ?? 0} km
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {editing && plan && (
        <Dialog open onOpenChange={(v) => { if (!v) setEditing(false); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit plan</DialogTitle></DialogHeader>
            <PlanForm initial={plan} floor={state.floor} submitLabel="Update plan" onSaved={() => { setEditing(false); load(); }} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Retailer location ──────────────────────────────────────────────────
export function SaveLocationButton({ retailer, onSaved }) {
  const [busy, setBusy] = useState(false);
  if (retailer.gps_lat != null && retailer.gps_lat !== "") return null;
  const save = async () => {
    setBusy(true);
    const fix = await getFix();
    if (!fix) {
      toast.error("Could not get your location. Turn phone location ON and try in an open place.");
      setBusy(false);
      return;
    }
    try {
      await dms.retailerSaveLocation(retailer.id, fix);
      toast.success("Shop location saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(detailOf(e, "Location was not saved"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="outline" disabled={busy} onClick={save} className="border-[#c9a227] text-[#8a6600] whitespace-normal h-auto py-1.5" data-testid={`save-location-${retailer.id}`}>
      <MapPin size={12} className="mr-1 shrink-0" /> {busy ? "Getting GPS…" : "Save shop location"}
    </Button>
  );
}

export function ResetLocationButton({ retailer, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!["owner", "super_admin"].includes(user?.role) || retailer.gps_lat == null || retailer.gps_lat === "") return null;
  const reset = async () => {
    setBusy(true);
    try {
      await dms.retailerResetLocation(retailer.id);
      toast.success("Location reset. The salesperson can save it again.");
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(detailOf(e, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="text-rose-700 border-rose-200" onClick={() => setOpen(true)} data-testid={`reset-location-${retailer.id}`}>
        <MapPin size={12} className="mr-1" /> Reset location
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset location of {retailer.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">The saved location will be removed and the change will be logged. The salesperson can save it again from the shop.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={reset} disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid="reset-location-confirm">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Targets: owner, TL, RM ─────────────────────────────────────────────
function TargetDialog({ person, onClose, onSaved }) {
  const [kind, setKind] = useState("daily");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const doc = person[kind] || {};
    setForm(Object.fromEntries(TARGET_KEYS.map((k) => [k, doc[k] ? String(doc[k]) : ""])));
  }, [kind, person]);
  const save = async () => {
    setBusy(true);
    try {
      await dms.spTargetSet(person.id, { type: kind, ...Object.fromEntries(TARGET_KEYS.map((k) => [k, Number(form[k]) || 0])) });
      toast.success("Target saved");
      onSaved();
    } catch (e) {
      toast.error(detailOf(e, "Target was not saved"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{person.name}: target</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {["daily", "monthly"].map((k) => (
            <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)} data-testid={`target-kind-${k}`}>
              {k === "daily" ? "Daily" : "Monthly"}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {TARGET_KEYS.map((k) => (
            <div key={k}>
              <Label>{TARGET_LABELS[k]}</Label>
              <Input type="number" min={0} className="mt-1" value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`target-${k}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">Saving with all fields empty removes this target. Without a daily target, the daily minimum is the monthly target divided by the working days in that month (all days except Sundays).</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="target-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SpTargetsPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    dms.spTargets().then((d) => setRows(d.data || [])).catch(() => toast.error("Could not load targets")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const summary = (doc) => (doc ? TARGET_KEYS.filter((k) => doc[k]).map((k) => fmtValue(k, doc[k])).join(" · ") : "—");
  return (
    <div>
      <PageHeader title="Salesperson Targets" subtitle="A salesperson can raise these targets in their plan, but not lower them" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Salesperson</TableHead><TableHead>Daily</TableHead><TableHead>Monthly</TableHead><TableHead>Today's minimum</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">No salespersons</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-xs">{summary(r.daily)}</TableCell>
                <TableCell className="text-xs">{summary(r.monthly)}</TableCell>
                <TableCell className="text-xs">{summary(r.floor_today)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`target-edit-${r.id}`}>Set</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {editing && <TargetDialog person={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ── Owner: Plan vs Actual ────────────────────────────────────────────────
export function PlanVsActualPage() {
  const nav = useNavigate();
  const [filters, setFilters] = useState({ date_from: "", date_to: "", salesperson_id: "", team_leader_id: "" });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    dms.planVsActual(params).then(setData).catch((e) => toast.error(detailOf(e, "Could not load the report"))).finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const rows = data?.data || [];
  const reasons = data?.reasons || {};
  const selectCls = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white";
  return (
    <div>
      <PageHeader title="Plan vs Actual" subtitle="Each salesperson's plan and what actually happened that day"
        action={<Button variant="outline" onClick={() => nav("/dms/owner/sp-targets")} data-testid="goto-targets"><Target size={14} className="mr-2" /> Targets</Button>} />
      <Card className="p-3 mb-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div><Label>From</Label><Input type="date" className="mt-1" value={filters.date_from || data?.date_from || ""} onChange={setF("date_from")} data-testid="pva-from" /></div>
        <div><Label>To</Label><Input type="date" className="mt-1" value={filters.date_to || data?.date_to || ""} onChange={setF("date_to")} data-testid="pva-to" /></div>
        <div><Label>Team</Label>
          <select className={selectCls} value={filters.team_leader_id} onChange={setF("team_leader_id")} data-testid="pva-team">
            <option value="">All teams</option>
            {(data?.team_leaders || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div><Label>Salesperson</Label>
          <select className={selectCls} value={filters.salesperson_id} onChange={setF("salesperson_id")} data-testid="pva-sp">
            <option value="">All salespersons</option>
            {(data?.salespersons || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Salesperson</TableHead><TableHead>Status</TableHead>
            {PVA_COLS.map((c) => <TableHead key={c.k}>{c.label}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">No data for this filter</TableCell></TableRow>}
            {!loading && rows.map((r) => {
              const key = `${r.salesperson_id}-${r.date}`;
              const isOpen = open === key;
              return (
                <React.Fragment key={key}>
                  <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(isOpen ? null : key)} data-testid={`pva-row-${key}`}>
                    <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                    <TableCell className="font-medium whitespace-nowrap">{r.name}</TableCell>
                    <TableCell className="text-xs">{STATUS_LABEL[r.status] || r.status}{r.report_deferred ? " · filled later option" : ""}{r.plan_edits > 0 ? ` · ${r.plan_edits} edit` : ""}</TableCell>
                    {PVA_COLS.map((c) => <TableCell key={c.k} className="text-xs"><PvaCell r={r} col={c} /></TableCell>)}
                  </TableRow>
                  {isOpen && (
                    <TableRow><TableCell colSpan={7} className="bg-slate-50">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          {r.answers && <div className="mb-3 bg-white rounded-lg"><SaidDidTable answers={r.answers} actual={r.self_actual} achieved={r.achieved} /></div>}
                          {(r.shops.length > 0 || !r.answers) && <>
                          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Planned shops</div>
                          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                            {r.shops.length === 0 && <div className="p-3 text-sm text-slate-400">No shops in the plan</div>}
                            {r.shops.map((s) => (
                              <ShopRow key={s.retailer_id} row={{ ...s, reason_label: s.reason ? `${reasons[s.reason] || s.reason}${s.reason_text ? `: ${s.reason_text}` : ""}` : null }} />
                            ))}
                          </div>
                          {r.plan_removed.length > 0 && <div className="text-xs text-rose-700 mt-2">Removed from plan: {r.plan_removed.join(", ")}</div>}
                          </>}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unplanned stops</div>
                          {r.unplanned.length === 0 && <div className="text-slate-400">None</div>}
                          {r.unplanned.map((s) => <div key={s.in_at}><b>{s.name}</b> <span className="text-xs text-slate-500">{timeOf(s.in_at)} – {timeOf(s.out_at)} · {s.minutes} min</span></div>)}
                          {r.unplanned.length > 0 && <div className="text-[11px] text-slate-400">{OSM_NOTE}</div>}
                          <div className="pt-2 text-xs text-slate-600 space-y-0.5">
                            <div>Punch: {r.in_at ? niceDate(r.in_at) : "—"} to {r.out_at ? niceDate(r.out_at) : "in progress"}</div>
                            {r.areas.length > 0 && <div>Areas: {r.areas.join(", ")}</div>}
                            {r.close_reason && r.close_reason !== "manual" && <div>Closed by: {r.close_reason === "report_later" ? "Fill report later option" : r.close_reason}</div>}
                            {r.plan_edits > 0 && <div>Plan edited {r.plan_edits} time(s). Original: {r.answers ? `${fmtItem("order_value", r.original_targets?.order_value)} · ${fmtItem("order_liters", r.original_targets?.order_liters)} · ${fmtItem("km_total", r.original_targets?.km)}` : TARGET_KEYS.map((k) => fmtValue(k, r.original_targets?.[k])).join(" · ")}</div>}
                            {r.remark && <div>Remark: {r.remark}</div>}
                          </div>
                        </div>
                      </div>
                    </TableCell></TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
